import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { twMerge } from "tailwind-merge";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "danger-solid"
  | "link";
export type ButtonSize = "sm" | "md" | "icon";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

export const BUTTON_BASE =
  "inline-flex min-w-11 sm:min-w-0 [@media(pointer:coarse)]:min-w-11 [@media(pointer:coarse)]:min-h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-xs cursor-pointer transition-colors hover:underline active:decoration-2 underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline";

export const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: "min-h-11 px-2.5 sm:h-8 sm:min-h-0",
  md: "min-h-11 px-3.5",
  icon: "w-11 min-h-11 px-0 sm:size-8 sm:min-h-0",
};

export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-od-primary font-semibold text-od-primary-contrast hover:bg-od-primary-hover active:bg-od-primary-active",
  secondary:
    "border border-od-border bg-transparent font-medium text-od-text hover:bg-od-surface-2",
  ghost:
    "bg-transparent font-medium text-od-muted hover:bg-od-surface-2 hover:text-od-text",
  danger:
    "border border-od-danger bg-transparent font-semibold text-od-danger hover:bg-od-surface-2",
  "danger-solid":
    "border border-transparent bg-od-danger font-semibold text-od-danger-contrast hover:bg-od-danger-hover active:bg-od-danger-active",
  link: "bg-transparent px-0 font-medium text-od-muted hover:text-od-text",
};

export const buttonClassName = (
  variant: ButtonVariant = "secondary",
  size: ButtonSize = "md",
  extra?: string
): string =>
  twMerge(BUTTON_BASE, BUTTON_SIZES[size], BUTTON_VARIANTS[variant], extra);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "secondary",
      size = "md",
      loading = false,
      disabled,
      className,
      type = "button",
      children,
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-size={size}
      className={buttonClassName(variant, size, className)}
      {...rest}
    >
      {loading && <CircularProgress size={12} sx={{ color: "inherit" }} />}
      {children}
    </button>
  )
);

Button.displayName = "Button";
